/**
 * SubHubLinks — a compact, crawlable nav block that removes "orphan sub-hub"
 * pages (e.g. /naati/compare, /en/notary/questions) from the internal link
 * graph. Plain <a> so it is present in the SSR HTML for crawlers and AI agents
 * without any client JS.
 */
export interface SubHubLink {
  href: string;
  label: string;
}

const DEFAULT_HEADING: Record<string, string> = {
  th: "หัวข้อเจาะลึกที่เกี่ยวข้อง",
  en: "Related in-depth guides",
  zh: "相关深度指南",
  ja: "関連する詳細ガイド",
  ko: "관련 심화 가이드",
  vi: "Hướng dẫn chuyên sâu liên quan",
  id: "Panduan mendalam terkait",
};

export function SubHubLinks({
  links,
  locale = "th",
  heading,
}: {
  links: SubHubLink[];
  locale?: string;
  heading?: string;
}) {
  const items = links.filter((l) => l.href && l.label);
  if (items.length === 0) return null;
  const title = heading ?? DEFAULT_HEADING[locale] ?? DEFAULT_HEADING.en!;
  return (
    <nav aria-label={title} className="mx-auto mt-10 max-w-6xl px-4">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <ul className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
        {items.map((l) => (
          <li key={l.href}>
            <a href={l.href} className="text-blue-700 hover:underline">
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Locale-aware labels for the two standard sub-hubs every cluster ships. */
const COMPARE_LABEL: Record<string, string> = {
  th: "เปรียบเทียบบริการที่ใกล้เคียง",
  en: "Compare with similar services",
  zh: "与相似服务对比",
  ja: "類似サービスとの比較",
  ko: "유사 서비스 비교",
  vi: "So sánh với dịch vụ tương tự",
  id: "Bandingkan dengan layanan serupa",
};

const QUESTIONS_LABEL: Record<string, string> = {
  th: "คลังคำถาม-คำตอบทั้งหมด",
  en: "Full question & answer library",
  zh: "完整问答库",
  ja: "よくある質問の全一覧",
  ko: "전체 질문·답변 모음",
  vi: "Toàn bộ hỏi & đáp",
  id: "Seluruh tanya & jawab",
};

/** Build the standard {compare, questions} sub-hub links for a cluster. */
export function clusterSubHubs(
  cluster: string,
  locale = "th",
  opts: { compare?: boolean; questions?: boolean } = { compare: true, questions: true },
): SubHubLink[] {
  const base = locale === "th" ? `/${cluster}` : `/${locale}/${cluster}`;
  const out: SubHubLink[] = [];
  if (opts.compare !== false) {
    out.push({ href: `${base}/compare`, label: COMPARE_LABEL[locale] ?? COMPARE_LABEL.en! });
  }
  if (opts.questions !== false) {
    out.push({ href: `${base}/questions`, label: QUESTIONS_LABEL[locale] ?? QUESTIONS_LABEL.en! });
  }
  return out;
}
